import { BadgeColor } from '@shared/components/ui/badge/badge';
import { ProgressBarColor } from '@shared/components/ui/progress-bar/progress-bar';
import { EstadoSeguimiento, Objetivos, SeguimientoObjetivo } from './objetivos.interface';

export const ESTADOS_SEGUIMIENTO: Record<EstadoSeguimiento, { label: string; color: BadgeColor }> = {
  no_evaluable: { label: 'No evaluable', color: 'light' },
  sin_meta_evaluable: { label: 'Sin meta evaluable', color: 'light' },
  cumplido: { label: 'Cumplido', color: 'success' },
  no_iniciado: { label: 'No iniciado', color: 'info' },
  en_curso: { label: 'En curso', color: 'primary' },
  pendiente_diagnostico: { label: 'Pendiente de diagnóstico', color: 'warning' },
  no_cumplido: { label: 'No cumplido', color: 'error' },
};

/** Estados que representan un objetivo con meta de preñeces medible contra un período válido. */
const ESTADOS_EVALUABLES: EstadoSeguimiento[] = ['cumplido', 'no_iniciado', 'en_curso', 'pendiente_diagnostico', 'no_cumplido'];

/** Estados que piden acción del equipo comercial: la meta no se alcanzó o falta cerrar diagnósticos. */
const ESTADOS_ATENCION: EstadoSeguimiento[] = ['no_cumplido', 'pendiente_diagnostico'];

export type TipoAvance = 'meta' | 'actividad' | 'sin_datos';

export interface AvanceVisual {
  tipo: TipoAvance;
  color: ProgressBarColor;
  /** Porcentaje real, puede pasar de 100 cuando se supera la meta. */
  porcentaje: number;
  /** "12 / 20" para metas de preñez; vacío en los otros casos. */
  conteo: string;
  /** Línea secundaria: cuántas faltan, cuánto se superó o por qué no se evalúa. */
  detalle: string;
}

/** Traduce el seguimiento a lo que hay que pintar en la fila, sin que la plantilla decida reglas. */
export function avanceVisual(objetivo: Objetivos): AvanceVisual {
  const seguimiento = objetivo.seguimiento;

  if (!seguimiento) {
    return { tipo: 'sin_datos', color: 'light', porcentaje: 0, conteo: '', detalle: 'Seguimiento no disponible' };
  }

  const color = colorProgreso(seguimiento.estado);

  if (seguimiento.porcentaje === null) {
    // Objetivo solo de aspiración (o sin meta evaluable): no hay meta contra la cual medir,
    // pero la actividad registrada sí es información útil antes de abrir el detalle.
    if (seguimiento.aspiraciones_realizadas !== null) {
      return {
        tipo: 'actividad',
        color,
        porcentaje: 0,
        conteo: `${seguimiento.aspiraciones_realizadas} aspiraciones`,
        detalle: `${seguimiento.donantes_aspiradas} donantes distintas`,
      };
    }

    return { tipo: 'sin_datos', color, porcentaje: 0, conteo: '', detalle: seguimiento.motivo ?? 'Sin meta evaluable' };
  }

  const faltante = seguimiento.faltante ?? 0;

  return {
    tipo: 'meta',
    color,
    porcentaje: seguimiento.porcentaje,
    conteo: `${seguimiento.preneces_positivas} / ${objetivo.cantidad_preneces_proyectadas}`,
    detalle: faltante > 0 ? `Faltan ${faltante}` : 'Meta alcanzada',
  };
}

function colorProgreso(estado: EstadoSeguimiento): ProgressBarColor {
  const color = ESTADOS_SEGUIMIENTO[estado].color;
  return color === 'dark' ? 'light' : color;
}

export interface ResumenObjetivos {
  total: number;
  evaluables: number;
  cumplidos: number;
  enCurso: number;
  atencion: number;
  /** Porcentaje de objetivos evaluables ya cumplidos. */
  porcentajeCumplidos: number;
  /** Hay objetivos que comparten trabajos entre sí: sus avances no son independientes. */
  hayCoincidencias: boolean;
}

/**
 * Resume la grilla contando OBJETIVOS por estado, nunca sumando preñeces entre objetivos:
 * dos objetivos con períodos solapados computan los mismos trabajos (`objetivos_coincidentes`),
 * así que un total agregado de producción sería doble conteo.
 */
export function resumenObjetivos(objetivos: Objetivos[]): ResumenObjetivos {
  const seguimientos = objetivos.map(objetivo => objetivo.seguimiento).filter((s): s is SeguimientoObjetivo => !!s);
  const evaluables = seguimientos.filter(s => ESTADOS_EVALUABLES.includes(s.estado)).length;
  const cumplidos = seguimientos.filter(s => s.estado === 'cumplido').length;

  return {
    total: objetivos.length,
    evaluables,
    cumplidos,
    enCurso: seguimientos.filter(s => s.estado === 'en_curso').length,
    atencion: seguimientos.filter(s => ESTADOS_ATENCION.includes(s.estado)).length,
    porcentajeCumplidos: evaluables ? Math.round(cumplidos / evaluables * 100) : 0,
    hayCoincidencias: seguimientos.some(s => s.objetivos_coincidentes.length > 0),
  };
}
